<template>
  <div class="page">
    <header class="page-header">
      <div>
        <p class="eyebrow">{{ t('winner') }}</p>
        <h1>{{ leaderboardData?.eventSlug || t('eventLoading') }}</h1>
        <p class="subtext" v-if="leaderboardData">Slug: {{ leaderboardData.eventSlug }}</p>
      </div>
      <router-link class="secondary" :to="eventLink">{{ t('backToEvent') }}</router-link>
    </header>

    <section v-if="isLoading" class="card">{{ t('loadingResults') }}</section>
    <section v-else-if="errorMessage" class="card error">{{ errorMessage }}</section>

    <section v-else class="card">
      <div v-if="winner" class="winner">
        <div>
          <p class="label">{{ t('topItem') }}</p>
          <h2>{{ winner.itemTitle }}</h2>
          <p class="subtext">{{ t('totalScore') }}: {{ winner.totalScore }}</p>
        </div>
        <span class="badge">{{ t('winner') }}</span>
      </div>
      <div v-else class="empty">{{ t('noScoredItems') }}</div>

      <h3>{{ t('allScoredItems') }}</h3>
      <ul class="list">
        <li v-for="item in scoredItems" :key="item.itemId">
          <span>{{ item.itemTitle }}</span>
          <strong>{{ item.totalScore }}</strong>
        </li>
      </ul>
    </section>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { getEventLeaderboard } from '../services/publicApi'
import { useI18n } from '../i18n'

export default {
  name: 'EventWinnerPage',
  setup() {
    const route = useRoute()
    const leaderboardData = ref(null)
    const isLoading = ref(true)
    const errorMessage = ref('')
    const { t } = useI18n()

    const loadEvent = async () => {
      isLoading.value = true
      errorMessage.value = ''
      try {
        leaderboardData.value = await getEventLeaderboard(route.params.eventSlug)
      } catch (error) {
        errorMessage.value = error.status === 404
          ? t('eventNotFound')
          : error.status === 400
            ? t('eventNotFinished')
            : t('loadResultsFailed')
      } finally {
        isLoading.value = false
      }
    }

    const scoredItems = computed(() => {
      if (!leaderboardData.value?.items) return []
      return leaderboardData.value.items
        .filter((item) => item.totalScore !== undefined && item.totalScore !== null)
        .sort((a, b) => b.totalScore - a.totalScore)
    })

    const winner = computed(() => scoredItems.value[0] || null)

    const eventLink = computed(() => ({
      name: 'event',
      params: { eventSlug: route.params.eventSlug }
    }))

    onMounted(loadEvent)

    return {
      leaderboardData,
      isLoading,
      errorMessage,
      t,
      scoredItems,
      winner,
      eventLink
    }
  }
}
</script>

<style scoped>
.page {
  display: grid;
  gap: 20px;
}

.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
}

.eyebrow {
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin: 0 0 8px;
}

.subtext {
  color: #64748b;
  margin: 4px 0 0;
}

.card {
  background: #ffffff;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
}

.media {
  display: grid;
  grid-template-columns: minmax(220px, 320px) 1fr;
  gap: 16px;
  margin-bottom: 20px;
  align-items: stretch;
}

.main-photo,
.banner-photo {
  border-radius: 16px;
  overflow: hidden;
  background: #e2e8f0;
}

.main-photo {
  aspect-ratio: 1 / 1;
}

.banner-photo {
  aspect-ratio: 3 / 1;
}

.main-photo img,
.banner-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.error {
  color: #b91c1c;
  border: 1px solid #fecaca;
  background: #fff1f2;
}

.winner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px;
  border-radius: 14px;
  background: #fef9c3;
  margin-bottom: 20px;
}

.label {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 12px;
  color: #a16207;
  margin: 0 0 6px;
}

.badge {
  background: #facc15;
  color: #854d0e;
  padding: 6px 12px;
  border-radius: 999px;
  font-weight: 700;
}

.empty {
  color: #64748b;
  margin-bottom: 16px;
}

.list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 10px;
}

.list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
}

.secondary {
  background: #e2e8f0;
  color: #0f172a;
  padding: 10px 16px;
  border-radius: 12px;
  text-decoration: none;
  font-weight: 600;
}
</style>
